import React, { useState } from 'react'
import { useRecordPracticalScore } from '@/hooks/useGrading'
import { PracticalScoreForm } from '@/components/grading/PracticalScoreForm'
import type { Enrollment } from '@/types/enrollment'

export interface PracticalScoreRowProps {
  enrollment: Enrollment
  trainerId: string
  scores: any[]
}

export const PracticalScoreRow: React.FC<PracticalScoreRowProps> = ({ enrollment, trainerId, scores }) => {
  const [rowError, setRowError] = useState('')
  const [rowSuccess, setRowSuccess] = useState('')

  const recordScore = useRecordPracticalScore()

  const practicalScores = Array.isArray(scores) ? scores : []
  const latestScore = practicalScores.length > 0 ? practicalScores[practicalScores.length - 1] : null

  const handleSubmitScore = async (data: { score: number; notes: string }) => {
    setRowError('')
    setRowSuccess('')

    try {
      await recordScore.mutateAsync({
        enrollmentId: enrollment.id,
        data: {
          trainerId,
          score: data.score,
          notes: data.notes,
        },
      })
      setRowSuccess('Score recorded.')
    } catch (err: any) {
      setRowError(err?.message || 'Failed to record score.')
    }
  }

  const studentName = enrollment.userName || enrollment.userId
  const studentEmail = enrollment.userEmail || ''

  return (
    <div className="rounded-2xl bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="size-10 rounded-full bg-gradient-to-br from-primary/40 to-primary/20 flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-primary">
              {studentName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
              {studentName}
            </p>
            {studentEmail && (
              <p className="text-xs text-gray-500 dark:text-text-muted truncate">
                {studentEmail}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {latestScore ? (
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500 text-white">
              Practical: {latestScore.score}/100
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-200/60 text-gray-500 dark:bg-white/5 dark:text-text-muted">
              No practical score
            </span>
          )}

          <span
            className={`px-2.5 py-1 rounded-full text-xs font-bold ${
              enrollment.status === 'completed'
                ? 'bg-emerald-500 text-white'
                : enrollment.status === 'active' || enrollment.status === 'in_progress'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200/60 text-gray-500 dark:bg-white/5 dark:text-text-muted'
            }`}
          >
            {enrollment.status}
          </span>
        </div>
      </div>

      <div className="mt-4">
        <PracticalScoreForm onSubmit={handleSubmitScore} isLoading={recordScore.isPending} />
      </div>

      {rowError && <p className="mt-2 text-xs text-red-500">{rowError}</p>}
      {rowSuccess && <p className="mt-2 text-xs text-green-500">{rowSuccess}</p>}
    </div>
  )
}
